import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Decimal from 'decimal.js';
import { Plus, Trash2, ShoppingCart, AlertCircle, Settings as SettingsIcon, Receipt as ReceiptIcon, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle, CardDescription } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Combobox } from '@/components/Combobox';
import { NumericInput } from '@/components/NumericInput';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/Dialog';
import { Kbd } from '@/components/Kbd';
import { api } from '@/lib/api';
import { money, date } from '@/lib/format';
import { toast } from '@/components/Toaster';

interface Item    { id: string; code: string; name: string; stock_uom: string; standard_rate: string; }
interface Account { id: string; name: string; account_number?: string; account_type?: string; }
interface Customer { id: string; name: string; display_name: string; }
interface Warehouse { id: string; name: string; code: string; is_group?: boolean; }
interface POSProfile {
  id: string; name: string;
  warehouse_id?: string;
  cash_account_id: string;
  default_customer_id?: string;
  default_tax_template_id?: string;
  income_account_id?: string;
}
interface POSInvoiceItem {
  id: string; row_index: number;
  item_id?: string; item_code: string; item_name: string;
  qty: string; uom: string; rate: string; amount: string; tax_amount: string; total: string;
}
interface POSInvoice {
  id: string; name: string;
  pos_profile_id: string;
  customer_id: string;
  posting_date: string;
  net_total: string; total_taxes_and_charges: string; grand_total: string;
  paid_amount: string;
  items: POSInvoiceItem[];
}

interface DraftLine {
  rowId: string;
  item_id: string;
  item_code: string;
  item_name: string;
  qty: string;
  uom: string;
  rate: string;
}

const rid = () => Math.random().toString(36).slice(2);
const PROFILE_STORAGE_KEY = 'logica.pos.profile_id';

/**
 * POS Register — single-screen cash-register experience.
 *
 * The POS backend exposes only POST endpoints (create profile, create+submit
 * invoice). There's no list of profiles, so the active profile ID is sticky
 * in localStorage. First-time setup is a small dialog that posts a new
 * profile via the API.
 */
export function POSRegister() {
  // ---- Sticky profile ID ----
  const [profileID, setProfileID] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(PROFILE_STORAGE_KEY) ?? '';
  });

  // Profile detail isn't a real backend endpoint — we just persist the id
  // and rely on the user to know which profile they configured. The setup
  // dialog stores the id+name pair from the create response.
  const [profileName, setProfileName] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(PROFILE_STORAGE_KEY + '.name') ?? '';
  });

  useEffect(() => {
    if (profileID) localStorage.setItem(PROFILE_STORAGE_KEY, profileID);
    else localStorage.removeItem(PROFILE_STORAGE_KEY);
    if (profileName) localStorage.setItem(PROFILE_STORAGE_KEY + '.name', profileName);
  }, [profileID, profileName]);

  // ---- Master data ----
  const { data: itemsResp } = useQuery({ queryKey: ['items'], queryFn: () => api<{ items: Item[] }>('/accounting/items') });
  const items = useMemo(() => (itemsResp?.items ?? []).filter((i) => true), [itemsResp]);
  const { data: customersResp } = useQuery({ queryKey: ['customers'], queryFn: () => api<{ items: Customer[] }>('/accounting/customers') });
  const customers = customersResp?.items ?? [];
  const customerOptions = useMemo(() => customers.map((c) => ({ value: c.id, label: c.display_name, description: c.name })), [customers]);

  // ---- Cart state ----
  const [lines, setLines]   = useState<DraftLine[]>([]);
  // Customer is picked per-sale. Sticky in localStorage so the cashier
  // doesn't reselect the walk-in customer on every reload. The backend
  // also falls back to the profile's default_customer_id if absent.
  const [customerID, setCustomerID] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(PROFILE_STORAGE_KEY + '.customer') ?? '';
  });
  useEffect(() => {
    if (customerID) localStorage.setItem(PROFILE_STORAGE_KEY + '.customer', customerID);
    else localStorage.removeItem(PROFILE_STORAGE_KEY + '.customer');
  }, [customerID]);
  const [lastReceipt, setLastReceipt] = useState<POSInvoice | null>(null);
  const [err, setErr]       = useState<string | null>(null);

  function addEmptyLine() {
    setLines((s) => [...s, { rowId: rid(), item_id: '', item_code: '', item_name: '', qty: '1', uom: 'Unit', rate: '0' }]);
  }
  function patch(rowId: string, p: Partial<DraftLine>) {
    setLines((s) => s.map((l) => l.rowId === rowId ? { ...l, ...p } : l));
  }
  function remove(rowId: string) {
    setLines((s) => s.filter((l) => l.rowId !== rowId));
  }
  function pickItem(rowId: string, itemID: string | null) {
    const it = items.find((i) => i.id === itemID);
    patch(rowId, {
      item_id: itemID ?? '',
      item_code: it?.code ?? '',
      item_name: it?.name ?? '',
      uom: it?.stock_uom ?? 'Unit',
      rate: it?.standard_rate ?? '0',
    });
  }
  function quickAdd(it: Item) {
    setLines((s) => {
      const existing = s.find((l) => l.item_id === it.id);
      if (existing) {
        return s.map((l) => l.rowId === existing.rowId
          ? { ...l, qty: new Decimal(l.qty || '0').plus(1).toString() }
          : l);
      }
      return [...s, {
        rowId: rid(),
        item_id: it.id,
        item_code: it.code,
        item_name: it.name,
        qty: '1',
        uom: it.stock_uom,
        rate: it.standard_rate,
      }];
    });
  }

  const total = useMemo(() => lines.reduce(
    (a, l) => a.plus(new Decimal(l.qty || '0').times(new Decimal(l.rate || '0'))),
    new Decimal(0),
  ), [lines]);

  const itemOptions = useMemo(() => items.map((it) => ({ value: it.id, label: it.name, description: it.code })), [items]);

  // ---- Charge ----
  const chargeMutation = useMutation({
    mutationFn: () => api<POSInvoice>('/pos/invoices', {
      method: 'POST',
      body: {
        pos_profile_id: profileID,
        customer_id: customerID || undefined,
        items: lines.filter((l) => l.item_id).map((l) => ({
          item_id: l.item_id, item_code: l.item_code, item_name: l.item_name,
          qty: l.qty, uom: l.uom, rate: l.rate,
        })),
      },
    }),
    onSuccess: (inv) => {
      toast.success('Sale recorded', `${inv.name} — ${money(inv.grand_total)}`);
      setLastReceipt(inv);
      setLines([]);
      setErr(null);
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Charge failed.'),
  });

  function onCharge() {
    setErr(null);
    if (!profileID) { setErr('Pick a POS Profile first.'); return; }
    const valid = lines.filter((l) => l.item_id);
    if (valid.length === 0) { setErr('Cart is empty.'); return; }
    chargeMutation.mutate();
  }

  // ---- Setup dialog ----
  const [setupOpen, setSetupOpen] = useState(false);

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'POS', to: '/pos' }, { label: 'Register' }]}
        title="POS Register"
        subtitle={profileName ? `Profile: ${profileName}` : 'Pick or create a POS Profile to start ringing sales.'}
        actions={
          <>
            <Button variant="ghost" onClick={() => setSetupOpen(true)}>
              <SettingsIcon className="size-4" /> POS Profile
            </Button>
          </>
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-4 max-w-[1400px]"
      >
        {/* LEFT: Cart + quick add */}
        <div className="space-y-4 min-w-0">
          {!profileID && (
            <Card className="!p-4 bg-warning/5 border-warning/30">
              <div className="flex items-start gap-2">
                <AlertCircle className="size-4 text-warning mt-0.5" />
                <div>
                  <CardTitle>No active POS Profile</CardTitle>
                  <CardDescription>Configure one once — cash + income accounts, default customer, tax template. It's sticky on this device.</CardDescription>
                  <Button className="mt-3" onClick={() => setSetupOpen(true)}>
                    <SettingsIcon className="size-4" /> Configure POS Profile
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {err && (
            <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
              <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
              <div className="text-body-sm text-brand-error">{err}</div>
            </Card>
          )}

          {/* Cart */}
          <Card padded={false}>
            <div className="flex items-center justify-between p-5 pb-3">
              <div>
                <CardTitle>Cart</CardTitle>
                <p className="text-caption text-stone mt-0.5">
                  Pick items from the catalog or add a custom line. Submit charges the cart in one step — POS invoices auto-submit.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={addEmptyLine}>
                <Plus className="size-4" /> Custom line
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
                  <tr>
                    <th className="text-left font-medium px-3 py-2 w-10">#</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[240px]">Item</th>
                    <th className="text-right font-medium px-3 py-2 w-24">Qty</th>
                    <th className="text-right font-medium px-3 py-2 w-32">Rate</th>
                    <th className="text-right font-medium px-3 py-2 w-32">Amount</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-10 text-stone text-center">
                      Empty cart. Add items below.
                    </td></tr>
                  )}
                  {lines.map((l, idx) => {
                    const amt = new Decimal(l.qty || '0').times(new Decimal(l.rate || '0'));
                    return (
                      <tr key={l.rowId} className="border-t border-hairline">
                        <td className="px-3 py-2 text-stone">{idx + 1}</td>
                        <td className="px-2 py-1.5">
                          <Combobox
                            options={itemOptions}
                            value={l.item_id || null}
                            onChange={(v) => pickItem(l.rowId, v)}
                            placeholder="Pick item…"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <NumericInput value={l.qty} onChange={(v) => patch(l.rowId, { qty: v })} decimalPlaces={0} />
                        </td>
                        <td className="px-2 py-1.5">
                          <NumericInput value={l.rate} onChange={(v) => patch(l.rowId, { rate: v })} decimalPlaces={0} />
                        </td>
                        <td className="px-3 py-2 text-right num">{money(amt.toString())}</td>
                        <td className="px-2 py-1.5">
                          <Button variant="ghost" size="icon" aria-label="Remove" onClick={() => remove(l.rowId)}>
                            <Trash2 className="size-3.5" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 sm:px-5 py-3 border-t border-hairline flex items-center justify-end gap-x-4 num">
              <div className="text-caption text-stone">Total</div>
              <div className="text-section-head font-medium">{money(total.toString())}</div>
            </div>
          </Card>

          {/* Quick catalog grid */}
          <Card>
            <CardTitle>Catalog</CardTitle>
            <CardDescription>Click an item to add or bump quantity.</CardDescription>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[360px] overflow-y-auto pr-1">
              {items.slice(0, 80).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => quickAdd(it)}
                  className="text-left rounded-md border border-hairline bg-canvas px-3 py-2 hover:bg-surface-soft transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                >
                  <div className="font-medium text-charcoal truncate">{it.name}</div>
                  <div className="flex items-center justify-between text-caption text-stone mt-1">
                    <span className="font-mono">{it.code}</span>
                    <span className="num">{money(it.standard_rate)}</span>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* RIGHT: Payment + last receipt */}
        <aside className="space-y-4">
          <Card>
            <CardTitle>Payment</CardTitle>
            <Field label="Customer" hint="Required unless the profile has a default (walk-in) customer.">
              <Combobox
                options={customerOptions}
                value={customerID || null}
                onChange={(v) => setCustomerID(v ?? '')}
                placeholder="Pick customer…"
                clearable
              />
            </Field>
            <div className="mt-3 space-y-3 text-body-sm">
              <div className="flex justify-between items-center">
                <span className="text-stone">Net total</span>
                <span className="num">{money(total.toString())}</span>
              </div>
              <div className="border-t border-hairline pt-3 flex justify-between items-baseline">
                <span className="text-charcoal font-medium">Grand total</span>
                <span className="text-section-head num font-semibold">{money(total.toString())}</span>
              </div>
            </div>
            <Button
              className="w-full mt-4 !py-3"
              onClick={onCharge}
              loading={chargeMutation.isPending}
              disabled={!profileID || lines.length === 0}
            >
              <ShoppingCart className="size-4" /> Charge <Kbd>↵</Kbd>
            </Button>
            <p className="mt-2 text-caption text-stone text-center">
              Posts cash + income GL entries on submit.
            </p>
          </Card>

          {lastReceipt && (
            <Card>
              <div className="flex items-center justify-between mb-2">
                <CardTitle>Last receipt</CardTitle>
                <Button variant="ghost" size="icon" aria-label="Clear last receipt" onClick={() => setLastReceipt(null)}>
                  <RotateCcw className="size-3.5" />
                </Button>
              </div>
              <div className="space-y-1.5 text-body-sm">
                <Row label="No." value={lastReceipt.name} mono />
                <Row label="Date" value={date(lastReceipt.posting_date)} />
                <Row label="Net"   value={money(lastReceipt.net_total)} />
                {Number(lastReceipt.total_taxes_and_charges) > 0 && (
                  <Row label="Tax" value={money(lastReceipt.total_taxes_and_charges)} />
                )}
                <Row label="Total" value={money(lastReceipt.grand_total)} />
              </div>
              <div className="mt-3 space-y-1 text-caption text-stone border-t border-hairline pt-2">
                {lastReceipt.items.map((it) => (
                  <div key={it.id} className="flex justify-between">
                    <span className="truncate">{it.item_name} × {it.qty}</span>
                    <span className="num">{money(it.total)}</span>
                  </div>
                ))}
              </div>
              <Button variant="secondary" className="w-full mt-3" onClick={() => window.print()}>
                <ReceiptIcon className="size-4" /> Print receipt
              </Button>
            </Card>
          )}
        </aside>
      </motion.div>

      <POSProfileSetupDialog
        open={setupOpen}
        onOpenChange={setSetupOpen}
        initialProfileID={profileID}
        initialProfileName={profileName}
        onSave={(p) => { setProfileID(p.id); setProfileName(p.name); setSetupOpen(false); }}
      />
    </>
  );
}

function POSProfileSetupDialog({
  open, onOpenChange, initialProfileID, initialProfileName, onSave,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initialProfileID: string;
  initialProfileName: string;
  onSave: (p: { id: string; name: string }) => void;
}) {
  // Three modes: pasted ID, or fill out the create form.
  const [mode, setMode] = useState<'reuse' | 'create'>('reuse');
  const [pasteID, setPasteID]     = useState(initialProfileID);
  const [pasteName, setPasteName] = useState(initialProfileName);

  // Create form
  const [name, setName]           = useState('');
  const [cashAcct, setCashAcct]   = useState('');
  const [incomeAcct, setIncomeAcct] = useState('');
  const [warehouseID, setWarehouseID] = useState('');
  const [defaultCust, setDefaultCust] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const { data: accountsResp }   = useQuery({ queryKey: ['accounts'],   queryFn: () => api<{ items: Account[]   }>('/accounting/accounts'),  enabled: open });
  const { data: customersResp }  = useQuery({ queryKey: ['customers'],  queryFn: () => api<{ items: Customer[]  }>('/accounting/customers'), enabled: open });
  const { data: warehousesResp } = useQuery({ queryKey: ['warehouses'], queryFn: () => api<{ items: Warehouse[] }>('/stock/warehouses'),     enabled: open });

  const accounts = accountsResp?.items ?? [];
  const customers = customersResp?.items ?? [];
  const warehouses = (warehousesResp?.items ?? []).filter((w) => !w.is_group);

  const cashAcctOptions   = accounts.filter((a) => a.account_type === 'cash' || a.account_type === 'bank').map((a) => ({ value: a.id, label: a.name, description: a.account_number }));
  const incomeAcctOptions = accounts.filter((a) => a.account_type === '' || a.account_type === undefined || !['cash','bank','receivable','payable','stock','fixed_asset','accumulated_depreciation','tax'].includes(a.account_type)).map((a) => ({ value: a.id, label: a.name, description: a.account_number }));
  const custOptions       = customers.map((c) => ({ value: c.id, label: c.display_name, description: c.name }));
  const whOptions         = warehouses.map((w) => ({ value: w.id, label: w.name, description: w.code }));

  const createMutation = useMutation({
    mutationFn: () => api<POSProfile>('/pos/profiles', { method: 'POST', body: {
      name,
      cash_account_id:     cashAcct,
      income_account_id:   incomeAcct || undefined,
      warehouse_id:        warehouseID || undefined,
      default_customer_id: defaultCust || undefined,
    } }),
    onSuccess: (p) => {
      toast.success('POS Profile created', p.name);
      onSave({ id: p.id, name: p.name });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Create failed.'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogTitle>POS Profile</DialogTitle>
        <DialogDescription>
          One per cashier / location. Stored on this device so reload doesn't lose it.
        </DialogDescription>
        <div className="mt-4">
          <div className="inline-flex rounded-md border border-hairline overflow-hidden mb-4">
            {([
              { v: 'reuse',  label: 'Use existing' },
              { v: 'create', label: 'Create new' },
            ] as const).map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setMode(opt.v)}
                className={'px-3 py-1.5 text-body-sm border-r last:border-r-0 border-hairline ' +
                  (mode === opt.v ? 'bg-brand-green text-on-primary' : 'bg-canvas text-charcoal hover:bg-surface-soft')}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {mode === 'reuse' && (
            <div className="space-y-3">
              <Field label="Profile ID" hint="Paste the id (pos_…) you got from /pos/profiles when you created the profile via the API.">
                <Input value={pasteID} onChange={(e) => setPasteID(e.target.value)} placeholder="pos_01..." className="font-mono text-caption" />
              </Field>
              <Field label="Display name" hint="Label shown in the page header. Free text.">
                <Input value={pasteName} onChange={(e) => setPasteName(e.target.value)} placeholder="Main register" />
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button onClick={() => {
                  if (!pasteID) { setErr('Paste a profile ID.'); return; }
                  onSave({ id: pasteID, name: pasteName || pasteID });
                }}>
                  Save
                </Button>
              </div>
            </div>
          )}

          {mode === 'create' && (
            <div className="space-y-3">
              {err && (
                <div className="text-body-sm text-brand-error">{err}</div>
              )}
              <Field label="Name" hint="Required.">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Main register" />
              </Field>
              <Field label="Cash account" hint="Where POS cash is deposited. Required.">
                <Combobox options={cashAcctOptions} value={cashAcct || null} onChange={(v) => setCashAcct(v ?? '')} placeholder="Pick cash/bank account…" />
              </Field>
              <Field label="Income account" hint="Default revenue account for sale lines.">
                <Combobox options={incomeAcctOptions} value={incomeAcct || null} onChange={(v) => setIncomeAcct(v ?? '')} placeholder="Pick income account…" clearable />
              </Field>
              <Field label="Default customer" hint="Used when the cashier doesn't pick one (walk-in customer).">
                <Combobox options={custOptions} value={defaultCust || null} onChange={(v) => setDefaultCust(v ?? '')} placeholder="— None —" clearable />
              </Field>
              <Field label="Warehouse" hint="Optional — sales source.">
                <Combobox options={whOptions} value={warehouseID || null} onChange={(v) => setWarehouseID(v ?? '')} placeholder="— None —" clearable />
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button onClick={() => createMutation.mutate()} loading={createMutation.isPending} disabled={!name || !cashAcct}>
                  Create and use
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-stone">{label}</span>
      <span className={mono ? 'font-mono text-caption text-charcoal' : 'text-charcoal num'}>{value}</span>
    </div>
  );
}

